import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { GenericCroutonProps, ShowCroutonArgs } from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/js-utils";

import { CROUTON_CLOSE_DELAY, OVERLAY_DELAYS } from "@checkout-core/constants/overlay";
import { isMROFlow } from "@checkout-core/helpers/MROCart";
import { getPriorityOfCurrentVSNewCrouton } from "@checkout-core/helpers/RegisterCrouton";

export const useRegisterCrouton = (
    setEnabledCroutonId: React.Dispatch<React.SetStateAction<{ enabledCroutonId: string }>>,
) => {
    const isMroFlow = isMROFlow();
    const [finalPriorityQueue, setFinalPriorityQueue] = useState<ShowCroutonArgs>();

    const croutonContent = useRef<GenericCroutonProps | null>(null);
    const listenersRef = useRef<Set<() => void>>(new Set());

    const setCroutonContent = useCallback((content: GenericCroutonProps | null) => {
        croutonContent.current = content;
        listenersRef.current.forEach((listener) => listener());
    }, []);

    const [shouldRenderCrouton, setShouldRenderCrouton] = useState(false);

    const croutonQueue = useRef<ShowCroutonArgs[]>([]); // Queue for croutons
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Timer for managing delay

    const subscribeToCroutonContent = useCallback((listener: () => void) => {
        listenersRef.current.add(listener);
        return () => {
            listenersRef.current.delete(listener);
        };
    }, []);
    const getCroutonContentSnapshot = useCallback(() => croutonContent.current, []);

    const registerCrouton = useCallback(
        (args: ShowCroutonArgs) => {
            const { forceRefreshContent = false } = args;
            if ((shouldRenderCrouton || !isEmpty(croutonContent?.current)) && !forceRefreshContent) {
                return;
            }
            // Clear any existing timer
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            // Add crouton to queue if not already present
            const idx = croutonQueue.current.findIndex((crouton) => crouton.id === args.id);
            if (idx === -1) {
                croutonQueue.current.push(args);
            } else if (forceRefreshContent) {
                croutonQueue.current[idx] = args; // replace with updated content
            }

            // Start timer to show highest priority crouton after delay
            timerRef.current = setTimeout(() => {
                if (isEmpty(croutonQueue.current)) {
                    return;
                }

                const priorityOptions = { useTimestampTiebreak: isMroFlow };
                let highestPriorityCrouton: ShowCroutonArgs = croutonQueue.current.reduce((prev, curr) =>
                    getPriorityOfCurrentVSNewCrouton(prev, curr, priorityOptions),
                );

                // MRO: already-mounted crouton must defend its slot by priority + timestamp.
                if (
                    isMroFlow &&
                    shouldRenderCrouton &&
                    !isEmpty(finalPriorityQueue) &&
                    finalPriorityQueue.id !== highestPriorityCrouton.id
                ) {
                    highestPriorityCrouton = getPriorityOfCurrentVSNewCrouton(
                        finalPriorityQueue,
                        highestPriorityCrouton,
                        priorityOptions,
                    );
                }

                setCroutonContent(highestPriorityCrouton?.content);
                setFinalPriorityQueue(highestPriorityCrouton);
                setShouldRenderCrouton(true);
                croutonQueue.current = []; // Reset queue after displaying
            }, OVERLAY_DELAYS.CROUTON);

            // Cleanup timer on unmount
            return () => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    croutonQueue.current = [];
                }
            };
        },
        [shouldRenderCrouton, setCroutonContent, isMroFlow, finalPriorityQueue],
    );

    const onCloseCrouton = useCallback(() => {
        const closeTimer = setTimeout(() => {
            setCroutonContent(null);
            setShouldRenderCrouton(false);
            croutonQueue.current = [];
        }, CROUTON_CLOSE_DELAY);

        return () => {
            clearTimeout(closeTimer);
            croutonQueue.current = [];
        };
    }, [setCroutonContent]);

    // This is used to close the crouton immediately without any delay
    const onCloseCroutonImmediate = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        setCroutonContent(null);
        setShouldRenderCrouton(false);
        croutonQueue.current = [];
        if (isMroFlow) {
            // Nothing is mounted after an immediate close, so the mounted-slot defense above must
            // stop defending the crouton that was just dropped (and enabledCroutonId must clear).
            setFinalPriorityQueue(undefined);
        }
    }, [isMroFlow, setCroutonContent]);

    const croutonProps = useMemo(
        () => ({
            registerCrouton,
            shouldRenderCrouton,
            onCloseCrouton,
            croutonContent: croutonContent.current,
            subscribeToCroutonContent,
            getCroutonContentSnapshot,
            enabledCroutonId: finalPriorityQueue?.id || "",
            onCloseCroutonImmediate,
        }),
        [
            registerCrouton,
            shouldRenderCrouton,
            onCloseCrouton,
            croutonContent,
            subscribeToCroutonContent,
            getCroutonContentSnapshot,
            finalPriorityQueue?.id,
            onCloseCroutonImmediate,
        ],
    );

    useEffect(() => {
        setEnabledCroutonId({
            enabledCroutonId: finalPriorityQueue?.id || "",
        });
    }, [finalPriorityQueue, setEnabledCroutonId]);

    return croutonProps;
};
